/**
 * 🧵 **THE VEFSTAÐUR — the upright warp-weighted loom. D136, T290.**
 *
 * 🎯 **haugur, 2026-10-05**, three questions and a *"yes"* to the table —
 * **warp first, then weave**; and 2026-10-06, *"loom A and stone B"*.
 *
 * | | His numbers |
 * |---|---|
 * | 📐 | **2 wide × 2 tall**, on a vefstofa's **back wall** (`d = 4`), facing the door's light |
 * | 🧵 warp | **4 þráður, 2 real minutes** — the threads and stones appear on the loom |
 * | 🧶 weave | **1 þráður of weft and 2 real minutes per ell**; **6 ells is one piece** |
 * | ⛽ | **no fuel, no light** — it keeps weaving while the world runs |
 * | 🪓 | taken down: the cloth and unused weft come back; **an unfinished warp is lost** |
 *
 * 📖 `Development/16_Clothes_And_The_Vefstofa.md` §5 has the history: a real
 * loom leaned on the long wall with basalt weights hanging from the warp, and
 * was woven from the TOP down — which is why the cloth grows down from the beam.
 *
 * ## 🔴 Vaðmál is counted in ELLS
 *
 * Each ell woven drops ONE `vadmal` into the cloth slot; a piece is six. That is
 * how the law counted it (Grágás: 6 ells = an eyrir, 120 = a cow), and it means
 * a half-woven piece is not worth nothing. ⚠️ **The Project Manager's reading of
 * the table**, told to haugur — one item per finished piece was the other.
 *
 * ## How "while you are away" works — spinning's way
 *
 * **Nothing ticks.** The block's row carries {@link LoomState}; whenever somebody
 * opens the loom, moves an item, the screen polls or the server's loom sweep
 * passes, {@link resolveLoom} works out what was woven since and writes it back.
 * ⚠️ **The clock is the GAME's**, which stops when nobody is in the world —
 * exactly as spinning and a búr's cooking do.
 *
 * ## Where it stands
 *
 * Four cells on the back row of a pit house, `d = 4`, two of `w = −1…+1`, at
 * `h = −1` and `h = 0` — the room is exactly 2 m high. The **anchor** is the
 * lower cell at the smaller `w`, which is the RIGHT one as you face the loom
 * (`burFrame`'s `right` is the player's LEFT walking in — see `pithouse.ts`).
 * It carries the house's `facing` and the picture (`weave`); the other three
 * cells are `vefstadurhluti`.
 */

import { blockIdForKey } from './blocks.ts';
import { BUR_FACINGS, burFrame, type BurFacing } from './bur.ts';
import { isPitDoor, pitCellAt, pitDoorHalf, pitFacing } from './pithouse.ts';
import { baseBlockOf, stateOf, withState } from './states.ts';
import { CLOTH_SLOT, LOOM_CLOTH, LOOM_THREAD, WARP_SLOT, WEFT_SLOT } from './loomslots.ts';

interface Cell {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export const LOOM_BLOCK = 'vefstadur';
export const LOOM_PART = 'vefstadurhluti';
export const LOOM_ITEM = 'vefstadur';

export {
  CLOTH_SLOT,
  LOOM_CLOTH,
  LOOM_THREAD,
  WARP_SLOT,
  WEFT_SLOT,
  loomSlotAccepts,
} from './loomslots.ts';

/** 🧵 **Threads in one warp.** 🎯 His number. */
export const WARP_THREADS = 4;
/** ⏱️ **Game hours to hang a warp** — two real minutes at 72× (one real minute is 1.2). 🎯 His number. */
export const WARP_GAME_HOURS = 2.4;
/** 🧶 **Weft threads per ell.** 🎯 His number. */
export const WEFT_PER_ELL = 1;
/** ⏱️ **Game hours per ell** — two real minutes. 🎯 His number. */
export const ELL_GAME_HOURS = 2.4;
/** 📏 **Ells in one piece** — an eyrir (Grágás). 🎯 His number. */
export const ELLS_PER_PIECE = 6;

/* ── the weaving ────────────────────────────────────────────────────────── */

/**
 * - `idle` — no warp on the loom.
 * - `warping` — four threads taken from the warp slot, being hung.
 * - `weaving` — a warp is on; `ells` of this piece are done.
 */
export type LoomStage = 'idle' | 'warping' | 'weaving';

/** The clock part of a loom's row. */
export interface LoomState {
  readonly stage: LoomStage;
  /**
   * When the step now under way began, in GAME hours — or `null` when the loom
   * waits (no warp thread, no weft, or the cloth slot full).
   */
  readonly fromGameHours: number | null;
  /** 📏 Ells woven on the warp now on the loom, 0–5. */
  readonly ells: number;
  /** 🧶 Has the weft for the ell under way already been taken off the stack? */
  readonly paid: boolean;
}

export const IDLE_LOOM: LoomState = { stage: 'idle', fromGameHours: null, ells: 0, paid: false };

/** A slot as the row holds it. Only the numbers this needs. */
interface Slot {
  readonly slot: number;
  readonly itemKey: string;
  readonly count: number;
}

function countIn<S extends Slot>(stacks: readonly S[], slot: number): number {
  return stacks.find((s) => s.slot === slot)?.count ?? 0;
}

/** A hair of tolerance — spinning's lesson: ten steps of 2.4 fall just short of 24. */
const EPS = 1e-9;

/**
 * 🧵 **Catch up on the weaving.**
 *
 * 1. **Idle** with 4 thread in the warp slot → they are taken and the warp is
 *    hung over {@link WARP_GAME_HOURS}.
 * 2. **Weaving** → each ell takes 1 weft thread when it BEGINS and lands one
 *    vaðmál in the cloth slot when it ends, {@link ELL_GAME_HOURS} later.
 * 3. After {@link ELLS_PER_PIECE} the piece is done and the loom is idle again —
 *    and the next warp starts at once, at the moment the piece was finished.
 *
 * ⏸️ **It waits** with no weft, or with the cloth slot full; ⚠️ **time spent
 * waiting is not saved up** (the búr's rule for a full shelf): the ell starts
 * when there is weft, or room, again. **Part of a step is kept** otherwise —
 * the clock moves by whole steps, never to "now".
 *
 * @param maxCloth how many vaðmál fit in one slot
 * @returns the new stacks and clock, and whether anything changed (the caller
 *   writes back only then)
 */
export function resolveLoom<S extends Slot>(
  stacks: readonly S[],
  state: LoomState,
  nowGameHours: number,
  maxCloth: number,
  make: (slot: number, itemKey: string, count: number) => S,
): { stacks: S[]; state: LoomState; changed: boolean } {
  let warp = countIn(stacks, WARP_SLOT);
  let weft = countIn(stacks, WEFT_SLOT);
  let cloth = countIn(stacks, CLOTH_SLOT);
  const before = { warp, weft, cloth };
  let { stage, ells, paid } = state;
  let from = state.fromGameHours;

  /* Every pass either spends a step of time, takes thread, or stops — so this
     ends. The bound is a belt over the braces: a week away is ~150 steps. */
  for (let guard = 0; guard < 100_000; guard++) {
    if (stage === 'idle') {
      if (warp < WARP_THREADS) {
        from = null;
        break;
      }
      warp -= WARP_THREADS;
      stage = 'warping';
      ells = 0;
      paid = false;
      if (from === null) from = nowGameHours;
      continue;
    }

    if (from === null) from = nowGameHours;

    if (stage === 'warping') {
      if (nowGameHours - from < WARP_GAME_HOURS - EPS) break;
      from += WARP_GAME_HOURS;
      stage = 'weaving';
      continue;
    }

    /* 🧶 weaving */
    if (!paid) {
      if (weft < WEFT_PER_ELL || cloth >= maxCloth) {
        /* ⏸️ Waiting — and the waiting is not saved up. */
        from = null;
        break;
      }
      weft -= WEFT_PER_ELL;
      paid = true;
    }
    if (nowGameHours - from < ELL_GAME_HOURS - EPS) break;
    if (cloth >= maxCloth) {
      from = null;
      break;
    }
    cloth += 1;
    ells += 1;
    paid = false;
    from += ELL_GAME_HOURS;
    if (ells >= ELLS_PER_PIECE) {
      /* ✂️ The piece is cut off; the next warp may start at this very moment. */
      stage = 'idle';
      ells = 0;
    }
  }

  const next: LoomState = { stage, fromGameHours: from, ells, paid };
  const changed =
    warp !== before.warp ||
    weft !== before.weft ||
    cloth !== before.cloth ||
    stage !== state.stage ||
    from !== state.fromGameHours ||
    ells !== state.ells ||
    paid !== state.paid;
  if (!changed) return { stacks: [...stacks], state, changed: false };

  const mine = new Set([WARP_SLOT, WEFT_SLOT, CLOTH_SLOT]);
  const out: S[] = stacks.filter((s) => !mine.has(s.slot));
  if (warp > 0) out.push(make(WARP_SLOT, LOOM_THREAD, warp));
  if (weft > 0) out.push(make(WEFT_SLOT, LOOM_THREAD, weft));
  if (cloth > 0) {
    const key = stacks.find((s) => s.slot === CLOTH_SLOT)?.itemKey ?? LOOM_CLOTH;
    out.push(make(CLOTH_SLOT, key, cloth));
  }
  return { stacks: out, state: next, changed: true };
}

/** 🧵 What the loom is doing, for the screen's sentence. */
export type LoomDoing = 'idle' | 'warping' | 'weaving' | 'noWeft' | 'clothFull';

/**
 * 🧵 **What the loom is doing, and how far into the step it is** (0–1) — for
 * the menu. Call it on a RESOLVED state.
 */
export function loomDoing(
  state: LoomState,
  nowGameHours: number,
  cloth: number,
  maxCloth: number,
): { doing: LoomDoing; part: number } {
  if (state.stage === 'idle') return { doing: 'idle', part: 0 };
  const length = state.stage === 'warping' ? WARP_GAME_HOURS : ELL_GAME_HOURS;
  if (state.fromGameHours === null) {
    if (state.stage === 'weaving' && cloth >= maxCloth) return { doing: 'clothFull', part: 0 };
    return { doing: 'noWeft', part: 0 };
  }
  const part = Math.min(1, Math.max(0, (nowGameHours - state.fromGameHours) / length));
  return { doing: state.stage, part };
}

/** 🖼️ The `weave` picture a state shows — what everybody sees without opening it. */
export type LoomWeave = 'bare' | 'warped' | 'ell1' | 'ell2' | 'ell3' | 'ell4' | 'ell5';

export function loomWeaveOf(state: LoomState): LoomWeave {
  if (state.stage === 'idle') return 'bare';
  if (state.stage === 'warping' || state.ells <= 0) return 'warped';
  return `ell${Math.min(5, state.ells)}` as LoomWeave;
}

/** 📏 How many ells of cloth a `weave` picture shows, 0–5, or −1 for a bare frame. */
export function ellsShown(weave: LoomWeave): number {
  if (weave === 'bare') return -1;
  if (weave === 'warped') return 0;
  return Number(weave.slice(3));
}

/* ── the blocks ─────────────────────────────────────────────────────────── */

/** 🧵 Is this id a loom's anchor, in any state? */
export function isLoom(id: number): boolean {
  const base = blockIdForKey(LOOM_BLOCK);
  return base !== undefined && baseBlockOf(id) === base;
}

/** 🧵 Is this id one of a loom's other three cells? */
export function isLoomPart(id: number): boolean {
  const part = blockIdForKey(LOOM_PART);
  return part !== undefined && id === part;
}

export function loomPartId(): number | undefined {
  return blockIdForKey(LOOM_PART);
}

/** 🧭 Which way a loom faces (its house's facing), or `undefined` for anything else. */
export function loomFacing(id: number): BurFacing | undefined {
  if (!isLoom(id)) return undefined;
  const f = stateOf(id)['facing'];
  return typeof f === 'string' && (BUR_FACINGS as readonly string[]).includes(f)
    ? (f as BurFacing)
    : 'north';
}

/** 🖼️ The picture a loom's anchor shows. */
export function loomWeave(id: number): LoomWeave {
  const w = stateOf(id)['weave'];
  return typeof w === 'string' && /^(bare|warped|ell[1-5])$/.test(w) ? (w as LoomWeave) : 'bare';
}

/** 🧵 The anchor block for a facing and a picture. */
export function loomIdFor(facing: BurFacing, weave: LoomWeave = 'bare'): number | undefined {
  const base = blockIdForKey(LOOM_BLOCK);
  return base === undefined ? undefined : withState(base, { facing, weave });
}

/**
 * 🧵 **A loom's four cells**, anchor first: the anchor, the cell to its left as
 * you face it (+w), and the two above them.
 */
export function loomCells(anchor: Cell, facing: BurFacing): Cell[] {
  const { right } = burFrame(facing);
  const left = { x: anchor.x + right.x, y: anchor.y, z: anchor.z + right.z };
  return [anchor, left, { ...anchor, y: anchor.y + 1 }, { ...left, y: left.y + 1 }];
}

/**
 * 🧵 **Which loom is this cell part of?** — its anchor, or `undefined`.
 * ⚠️ Asked of the block, never assumed: two looms in two houses back to back
 * stand a wall apart, and a part is only ever part of the anchor that names it.
 */
export function loomAnchorFor(
  cell: Cell,
  blockAt: (x: number, y: number, z: number) => number,
): Cell | undefined {
  const here = blockAt(cell.x, cell.y, cell.z);
  if (isLoom(here)) return cell;
  if (!isLoomPart(here)) return undefined;
  for (const facing of BUR_FACINGS) {
    const { right } = burFrame(facing);
    for (const across of [0, 1])
      for (const up of [0, 1]) {
        const a = { x: cell.x - right.x * across, y: cell.y - up, z: cell.z - right.z * across };
        /* ⚠️ Only the FACING needs asking: an anchor one step back and one down
           along that facing always has this cell among its four — a second
           check that it does was removed because no world could make it fail. */
        if (loomFacing(blockAt(a.x, a.y, a.z)) === facing) return a;
      }
  }
  return undefined;
}

/** Where a loom may go, or why not. */
export type LoomPlot =
  | {
      readonly verdict: 'free';
      readonly anchor: Cell;
      readonly facing: BurFacing;
      readonly door: Cell;
    }
  | { readonly verdict: 'notOnBackWall' | 'blocked' };

/** The back row, `d = 4`, and its pair of `w` for a loom's anchor (the smaller `w`). */
const BACK_ROW = 4;
const LOOM_W = [-1, 0, 1] as const;

/**
 * 🟫 **Where would a loom go, aimed at this air cell?** — the anchor and facing,
 * or why not.
 *
 * The aimed cell must be on a vefstofa's back row (`d = 4`, `w = −1…+1`, at the
 * floor or a metre up) — found by asking which pit-house door would put it
 * there. Aimed at either end, the loom covers that end and the middle; aimed at
 * the middle, it stands **away from the oven** (`w = 0…+1`, the oven is at
 * `w = −2`) if that pair is clear.
 *
 * ⚠️ **One function for the client's shadow and the server's refusal**, so the
 * two can never disagree — the rule `plotVerdict` keeps for the houses.
 */
export function loomPlotFor(
  aimed: Cell,
  blockAt: (x: number, y: number, z: number) => number,
): LoomPlot {
  for (const facing of BUR_FACINGS) {
    const { into, right } = burFrame(facing);
    for (const h of [-1, 0])
      for (const w of LOOM_W) {
        const door = {
          x: aimed.x - into.x * BACK_ROW - right.x * w,
          y: aimed.y - h,
          z: aimed.z - into.z * BACK_ROW - right.z * w,
        };
        const id = blockAt(door.x, door.y, door.z);
        if (!isPitDoor(id) || pitDoorHalf(id) !== 'lower' || pitFacing(id) !== facing) continue;
        const pairs = w === -1 ? [-1] : w === 1 ? [0] : [0, -1];
        for (const w0 of pairs) {
          const anchor = pitCellAt(door, facing, BACK_ROW, w0, -1);
          const clear = loomCells(anchor, facing).every((c) => blockAt(c.x, c.y, c.z) === 0);
          if (clear) return { verdict: 'free', anchor, facing, door };
        }
        return { verdict: 'blocked' };
      }
  }
  return { verdict: 'notOnBackWall' };
}

/**
 * 📍 **The middle of the loom's floor**, in cell units — between its two
 * columns, at the anchor's height. Add 0.5 to x and z for a cell's own centre,
 * as `burCentre` asks.
 */
export function loomCentre(anchor: Cell, facing: BurFacing): { x: number; y: number; z: number } {
  const { right } = burFrame(facing);
  return { x: anchor.x + right.x * 0.5, y: anchor.y, z: anchor.z + right.z * 0.5 };
}

/**
 * 🧍 **Is anybody standing where the loom would go?** — its four cells, against
 * the bodies of players and animals. Placing over a body is refused, as a gate
 * refuses to shut on one.
 */
export function loomBodyInside(
  anchor: Cell,
  facing: BurFacing,
  bodies: readonly {
    readonly x: number;
    readonly y: number;
    readonly z: number;
    readonly half: number;
    readonly height: number;
  }[],
): boolean {
  const cells = loomCells(anchor, facing);
  const minX = Math.min(...cells.map((c) => c.x));
  const maxX = Math.max(...cells.map((c) => c.x)) + 1;
  const minZ = Math.min(...cells.map((c) => c.z));
  const maxZ = Math.max(...cells.map((c) => c.z)) + 1;
  const minY = anchor.y;
  const maxY = anchor.y + 2;
  return bodies.some(
    (o) =>
      o.x + o.half > minX &&
      o.x - o.half < maxX &&
      o.z + o.half > minZ &&
      o.z - o.half < maxZ &&
      o.y < maxY &&
      o.y + o.height > minY,
  );
}
